import { useCallback, useEffect, useState } from "react";
import { ClientSafeHandoffViewV1Schema } from "@enrein/shared-contracts";
import { toClientApiErrorMessage, toHandoffIssuanceLabel } from "@enrein/shared-contracts/display/tier1-client-display";
import { V10HandoffEvidenceHub } from "../components/V10HandoffEvidenceHub.js";
import type { V10ClientLinkViewProps } from "../types.js";

export function V10HandoffTab({
  active,
  api,
  linkId,
  token,
  deployEnv,
}: {
  readonly active: boolean;
  readonly api: V10ClientLinkViewProps["api"];
  readonly linkId: string;
  readonly token: string;
  readonly deployEnv: V10ClientLinkViewProps["deployEnv"];
}) {
  const [error, setError] = useState<string | null>(null);
  const [downloadAvailable, setDownloadAvailable] = useState(false);
  const [issuanceLabel, setIssuanceLabel] = useState("Preparing handoff");

  const load = useCallback(async () => {
    if (!active) return;
    try {
      const view = ClientSafeHandoffViewV1Schema.parse(await api.getHandoff(linkId, token));
      setDownloadAvailable(view.downloadAvailable);
      setIssuanceLabel(toHandoffIssuanceLabel(view.issuanceStatus));
      setError(null);
    } catch (err) {
      setDownloadAvailable(false);
      setError(err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)));
    }
  }, [active, api, linkId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function download() {
    try {
      const blob = await api.downloadHandoff(linkId, token);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "client-safe-handoff.zip";
      link.rel = "noreferrer";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)));
    }
  }

  return (
    <section
      className={`v10-cl-tab-panel${active ? " active" : ""}`}
      role="tabpanel"
      id="v10-tab-handoff"
      aria-labelledby="v10-tab-btn-handoff"
      hidden={!active}
    >
      <div className={`v10-cl-card${downloadAvailable ? "" : " v10-cl-handoff-lock"}`}>
        <h3>{downloadAvailable ? "Handoff ready" : "Handoff is locked for now"}</h3>
        <p id="v10-handoff-reason">{issuanceLabel}</p>
        {error ? <p className="v10-cl-muted">{error}</p> : null}
        <button
          type="button"
          className="v10-cl-btn"
          disabled={!downloadAvailable}
          aria-describedby="v10-handoff-reason"
          onClick={() => void download()}
        >
          Download handoff
        </button>
      </div>
      <V10HandoffEvidenceHub active={active} api={api} linkId={linkId} token={token} deployEnv={deployEnv} />
    </section>
  );
}
